'use es6';

import { useState, useMemo, Component, createElement, useRef, useEffect } from 'react';
import hoistStatics from 'hoist-non-react-statics';

/**
 * Copyright (c) 2013-present, Facebook, Inc.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

/**
 * Use invariant() to assert state which your program assumes to be true.
 *
 * Provide sprintf-style format (only %s is supported) and arguments
 * to provide information about what broke and what you were
 * expecting.
 *
 * The invariant message will be stripped in production, but the invariant
 * will remain to ensure logic does not differ in production.
 */

var invariant = function invariant(condition, format, a, b, c, d, e, f) {
  if (process.env.NODE_ENV !== 'production') {
    if (format === undefined) {
      throw new Error('invariant requires an error message argument');
    }
  }
  if (!condition) {
    var error;
    if (format === undefined) {
      error = new Error('Minified exception occurred; use the non-minified dev environment ' + 'for the full error message and additional helpful warnings.');
    } else {
      var args = [a, b, c, d, e, f];
      var argIndex = 0;
      error = new Error(format.replace(/%s/g, function () {
        return args[argIndex++];
      }));
      error.name = 'Invariant Violation';
    }
    error.framesToPop = 1; // we don't care about invariant's own frame
    throw error;
  }
};
var browser = invariant;
function isDispatcher(dispatcher) {
  return !!dispatcher && typeof dispatcher === 'object' && typeof dispatcher.register === 'function' && typeof dispatcher.unregister === 'function';
}
function enforceDispatcher(dispatcher) {
  browser(isDispatcher(dispatcher), 'expected `dispatcher` to be a `Flux.Dispatcher` or compatible object but' + 'got `%s` Learn more about the dispatcher interface:' + ' https://github.com/HubSpot/general-store#dispatcher-interface', dispatcher);
}
function isPayload(payload) {
  return payload !== null && typeof payload === 'object' && (typeof payload.actionType === 'string' || typeof payload.type === 'string');
}
let instance = null;
function get() {
  return instance;
}
function set(dispatcher) {
  browser(isDispatcher(dispatcher), 'DispatcherInstance.set: Expected dispatcher to be an object' + ' with a register method, and an unregister method but got "%s".' + ' Learn more about the dispatcher interface:' + ' https://github.com/HubSpot/general-store#dispatcher-interface', dispatcher);
  instance = dispatcher;
}
function clear() {
  const oldInstance = instance;
  instance = null;
  return oldInstance;
}
var DispatcherInstance = /*#__PURE__*/Object.freeze({
  __proto__: null,
  get: get,
  set: set,
  clear: clear
});
class EventHandler {
  constructor(instance, key) {
    this._key = key;
    this._instance = instance;
  }
  getKey() {
    return this._key;
  }
  remove() {
    if (this._instance === null || this._instance === undefined) {
      return;
    }
    this._instance.removeHandler(this._key);
    this._instance = null;
  }
}
const PREFIX = 'uid_';
let nextUid = 0;
function uid() {
  return PREFIX + nextUid++;
}
class Event {
  constructor() {
    this._handlers = {};
  }
  /**
   * Add a subscription to this event
   *
   * @param  callback  run when the event is triggered.
   * @return this
   */
  addHandler(callback) {
    const key = uid();
    this._handlers[key] = callback;
    return new EventHandler(this, key);
  }
  /**
   * Destroys this event. Removes all handlers.
   *
   * @return this
   */
  remove() {
    this._handlers = {};
    return this;
  }
  /**
   * Removes a subscription by key.
   *
   * @param  key   id of the subscription to remove
   * @return this
   */
  removeHandler(key) {
    delete this._handlers[key];
    return this;
  }
  /**
   * @protected
   * Run a handler by key if it exists
   *
   * @param  key  id of the handler to run
   */
  _runHandler(key) {
    if (this._handlers.hasOwnProperty(key)) {
      this._handlers[key].call(null);
    }
  }
  /**
   * Run all subscribed handlers.
   *
   * @return this
   */
  runHandlers() {
    Object.keys(this._handlers).forEach(this._runHandler.bind(this));
    return this;
  }
  /**
   * Convenience method for running multiple events.
   *
   * @param  events  a list of events to run.
   */
  static runMultiple(events) {
    events.forEach(evt => evt.runHandlers());
  }
}
const HINT_LINK = 'Learn more about using the Store API:' + ' https://github.com/HubSpot/general-store#using-the-store-api';
const hasReduxDevTools = typeof window !== 'undefined' && window.__REDUX_DEVTOOLS_EXTENSION__;
function getNull() {
  return null;
}
class Store {
  constructor({
    dispatcher,
    factory,
    getter,
    initialState,
    name,
    responses
  }) {
    this._dispatcher = dispatcher;
    this._factory = factory;
    this._getter = getter;
    this._name = name || 'Store';
    this._state = initialState;
    this._responses = responses;
    this._event = new Event();
    this._uid = uid();
    this._dispatchToken = this._dispatcher.register(this._handleDispatch.bind(this));
    if (hasReduxDevTools) {
      this._devToolsExtension = window.__REDUX_DEVTOOLS_EXTENSION__.connect({
        name: name || `Store_${this._uid}`,
        instanceId: this._uid
      });
      this._unsubscribeDevTools = this._devToolsExtension.subscribe(message => {
        if (message.type === 'DISPATCH' && message.payload.type === 'JUMP_TO_ACTION') {
          this._state = JSON.parse(message.state);
          this.triggerChange();
        }
      });
    }
  }
  /**
   * Subscribe to changes on this store.
   *
   * @param  callback  will run every time the store responds to a dispatcher
   * @return this
   */
  addOnChange(callback) {
    browser(typeof callback === 'function', 'Store.addOnChange: expected callback to be a function' + ' but got "%s" instead. %s', callback, HINT_LINK);
    return this._event.addHandler(callback);
  }
  /**
   * Returns the store's referenced value
   *
   * @param  ...  accepts any number of params
   * @return any
   */
  get(...args) {
    return this._getter(this._state, ...args);
  }
  /**
   * @protected
   * Responds to incoming messages from the Dispatcher
   */
  _handleDispatch(payload) {
    if (process.env.NODE_ENV !== 'production') {
      browser(isPayload(payload), 'Store: expected dispatched payload to be an object with a' + ' property "actionType" containing a string and an optional property' + ' "data" containing any value but got "%s" instead. Learn more about' + ' the dispatcher interface:' + ' https://github.com/HubSpot/general-store#dispatcher-interface');
    }
    const actionType = payload.actionType || payload.type;
    const data = payload.hasOwnProperty('data') ? payload.data : payload.payload;
    if (!actionType || !this._responses.hasOwnProperty(actionType)) {
      return;
    }
    this._state = this._responses[actionType](this._state, data, actionType, payload);
    this.triggerChange();
    if (this._devToolsExtension) {
      this._devToolsExtension.send(actionType, this._state);
    }
  }
  /**
   * Destroys this instance of the store.
   * Dispatch callback is unregistered. Subscriptions are removed.
   */
  remove() {
    this._dispatcher.unregister(this._dispatchToken);
    this._event.remove();
    this._getter = getNull;
    this._responses = {};
    typeof this._unsubscribeDevTools === 'function' && this._unsubscribeDevTools();
    typeof this._devToolsExtension !== 'undefined' && this._devToolsExtension.unsubscribe();
  }
  toString() {
    return `${this._name}<${this._state}>`;
  }
  /**
   * Runs all of the store's subscription callbacks
   *
   * @return this
   */
  triggerChange() {
    if (process.env.NODE_ENV !== 'production') {
      if (!this._dispatcher.isDispatching()) {
        console.warn('Store: you called store.triggerChange() outside of a' + ' dispatch loop. Send an action trough the dispatcher to' + ' avoid potentailly confusing behavior.');
      }
    }
    this._event.runHandlers();
    return this;
  }
}
function getActionTypes(store) {
  return Object.keys(store._responses) || [];
}
function getDispatcher(store) {
  return store._dispatcher;
}
function getDispatchToken(store) {
  return store._dispatchToken;
}
function getGetter(store) {
  return store._getter;
}
function getId(store) {
  return store._uid;
}
function getFactory(store) {
  return store._factory;
}
function getName(store) {
  return store._name;
}
function getResponses(store) {
  return store._responses;
}
function getState(store) {
  return store._state;
}
function isStore(store) {
  return store instanceof Store;
}
var InspectStore = /*#__PURE__*/Object.freeze({
  __proto__: null,
  getActionTypes: getActionTypes,
  getDispatcher: getDispatcher,
  getDispatchToken: getDispatchToken,
  getGetter: getGetter,
  getId: getId,
  getFactory: getFactory,
  getName: getName,
  getResponses: getResponses,
  getState: getState,
  isStore: isStore
});
const HINT_LINK$1 = 'Learn more about defining stores:' + ' https://github.com/HubSpot/general-store#create-a-store';
function enforceResponse(existingResponses, actionType, response) {
  browser(typeof actionType === 'string', 'StoreFactory.defineResponses: expected actionType to be a string' + ' but got "%s" instead. %s', actionType, HINT_LINK$1);
  browser(!existingResponses.hasOwnProperty(actionType), 'StoreFactory.defineResponses: conflicting resposes for actionType' + ' "%s". Only one response can be defined per actionType per Store. %s', actionType, HINT_LINK$1);
  browser(typeof response === 'function', 'StoreFactory.defineResponses: expected response to be a function' + ' but got "%s" instead. %s', response);
}
function defaultGetInitialState() {
  return undefined;
}
function defaultGetter(state) {
  return state;
}
class StoreFactory {
  constructor({
    getter,
    getInitialState,
    name,
    responses
  } = {}) {
    this._definition = {
      getter: getter || defaultGetter,
      getInitialState: getInitialState || defaultGetInitialState,
      name,
      responses: responses || {}
    };
  }
  defineGet(getter) {
    browser(this._definition.getter === defaultGetter, 'StoreFactory.defineGet: a getter is already defined.');
    return new StoreFactory(Object.assign(Object.assign({}, this._definition), {
      getter
    }));
  }
  defineGetInitialState(getInitialState) {
    browser(typeof getInitialState === 'function', 'StoreFactory.defineGetInitialState: getInitialState must be a function.');
    return new StoreFactory(Object.assign(Object.assign({}, this._definition), {
      getInitialState
    }));
  }
  defineName(name) {
    const currentName = this._definition.name;
    return new StoreFactory(Object.assign(Object.assign({}, this._definition), {
      name: currentName ? `${name}(${currentName})` : name
    }));
  }
  defineResponses(newResponses) {
    const {
      responses
    } = this._definition;
    browser(newResponses && typeof newResponses === 'object', 'StoreFactory.defineResponses: newResponses must be an object');
    Object.keys(newResponses).forEach(actionType => enforceResponse(responses, actionType, newResponses[actionType]));
    return new StoreFactory(Object.assign(Object.assign({}, this._definition), {
      responses: Object.assign(Object.assign({}, responses), newResponses)
    }));
  }
  defineResponseTo(actionTypes, response) {
    return this.defineResponses([].concat(actionTypes).reduce((responses, actionType) => {
      responses[actionType] = response;
      return responses;
    }, {}));
  }
  getDefinition() {
    return this._definition;
  }
  register(dispatcher) {
    dispatcher = dispatcher || get();
    browser(dispatcher !== null && typeof dispatcher === 'object', "StoreFactory.register: you haven't provided a dispatcher instance." + ' You can pass an instance to' + ' GeneralStore.define().register(dispatcher) or use' + ' GeneralStore.DispatcherInstance.set(dispatcher) to set a global' + ' instance.' + ' https://github.com/HubSpot/general-store#default-dispatcher-instance');
    enforceDispatcher(dispatcher);
    const {
      getter,
      getInitialState,
      name,
      responses
    } = this._definition;
    return new Store({
      dispatcher,
      factory: this,
      getter,
      initialState: getInitialState(),
      name,
      responses
    });
  }
}
const HINT_LINK$2 = 'Learn more about defining stores:' + ' https://github.com/HubSpot/general-store#create-a-store';
function dropFirstArg(func) {
  return (head, ...tail) => func(...tail);
}
class StoreSingleton {
  constructor() {
    this._facade = null;
    this._factory = new StoreFactory({
      getter: (state, ...args) => {
        if (typeof this._getter !== 'function') {
          return undefined;
        }
        return this._getter(...args);
      }
    });
    this._getter = null;
  }
  defineGet(getter) {
    browser(!this.isRegistered(), 'StoreSingleton.defineGet: this store definition cannot be modified' + ' because it has already been registered with a dispatcher. %s', HINT_LINK$2);
    browser(typeof getter === 'function', 'StoreSingleton.defineGet: expected getter to be a function but got' + ' "%s" instead. %s', getter, HINT_LINK$2);
    this._getter = getter;
    return this;
  }
  defineName(name) {
    this._factory.defineName(name);
    return this;
  }
  defineResponseTo(actionTypes, response) {
    browser(!this.isRegistered(), 'StoreSingleton.defineResponseTo: this store definition cannot be' + ' modified because is has already been registered with a dispatcher. %s', HINT_LINK$2);
    this._factory = this._factory.defineResponseTo(actionTypes, dropFirstArg(response));
    return this;
  }
  isRegistered() {
    return this._facade instanceof Store;
  }
  register(dispatcher) {
    browser(typeof this._getter === 'function', 'StoreSingleton.register: a store cannot be registered without a' + ' getter. Use GeneralStore.define().defineGet(getter) to define a' + ' getter. %s', HINT_LINK$2);
    if (!this._facade) {
      this._facade = this._factory.register(dispatcher);
    }
    return this._facade;
  }
}
function oForEach(subject, operation) {
  for (const key in subject) {
    if (subject.hasOwnProperty(key)) {
      operation(subject[key], key);
    }
  }
}
function oMap(subject, mapper) {
  const result = {};
  oForEach(subject, (value, key) => {
    result[key] = mapper(value, key);
  });
  return result;
}
function oMerge(subject, updates) {
  oForEach(updates, (value, key) => {
    subject[key] = value;
  });
  return subject;
}
function oReduce(subject, reducer, initialAcc) {
  let result = initialAcc;
  oForEach(subject, (value, key) => {
    result = reducer(result, value, key);
  });
  return result;
}
function isImmutable(obj) {
  return obj && typeof obj.hashCode === 'function' && typeof obj.equals === 'function';
}
function _compareArrays(arr1, arr2) {
  if (!arr1 || !arr2) return false;
  if (arr1.length !== arr2.length) {
    return false;
  }
  if (!arr1.length) {
    return true;
  }
  for (const idx in arr1) {
    // this makes it _technically_ not a shallow equal for array values,
    // but for stores that return arrays of objects, the object identities
    // will likely not be equal so we should actually inspect the objects
    // to see if they're equal
    if (!shallowEqual(arr1[idx], arr2[idx])) {
      return false;
    }
  }
  return true;
}
// polyfill for Object.is
function objectIs(x, y) {
  return x === y && (x !== 0 || 1 / x === 1 / y) || x !== x && y !== y // eslint-disable-line no-self-compare
  ;
}
function shallowEqual(obj1, obj2) {
  if (objectIs(obj1, obj2)) {
    return true;
  }
  // Special handling for FB Immutables, as they must be handled specially.
  // While this technically means this is deep equality for Immutables,
  // it's better to have a more specific check than an entirely incorrect one.
  if (isImmutable(obj1) && isImmutable(obj2)) {
    return obj1.equals(obj2);
  }
  if (typeof obj1 !== typeof obj2) {
    return false;
  }
  // would have passed === check if both were same falsy value
  if (!obj1 || !obj2) {
    return false;
  }
  if (typeof obj1 !== 'object') {
    return false;
  }
  if (Object.keys(obj1).length !== Object.keys(obj2).length) {
    return false;
  }
  for (const property in obj1) {
    if (obj1.hasOwnProperty(property) && !obj2.hasOwnProperty(property)) {
      return false;
    }
    if (!obj1.hasOwnProperty(property) && obj2.hasOwnProperty(property)) {
      return false;
    }
    if (obj1.hasOwnProperty(property) && obj2.hasOwnProperty(property)) {
      if (isImmutable(obj1[property]) && isImmutable(obj2[property])) {
        if (!obj1[property].equals(obj2[property])) {
          return false;
        }
      } else if (Array.isArray(obj1[property]) || Array.isArray(obj2[property])) {
        return _compareArrays(obj1[property], obj2[property]);
      } else if (!objectIs(obj1[property], obj2[property])) {
        return false;
      }
    }
  }
  return true;
}
function enforceValidDependencies(dependencies) {
  browser(dependencies && typeof dependencies === 'object', 'expected `dependencies` to be an `object` but got `%s`', dependencies);
  oForEach(dependencies, (dependency, field) => {
    if (dependency instanceof Store) {
      return;
    }
    browser(dependency && typeof dependency === 'object', 'expected `%s` to be an `object` but got `%s`', field, dependency);
    const {
      deref,
      stores
    } = dependency;
    browser(typeof deref === 'function', 'expected `%s.deref` to be a function but got `%s`', field, deref);
    browser(Array.isArray(stores), 'expected `%s.stores` to be an Array but got `%s`', field, dependency.stores);
    stores.forEach((store, index) => {
      browser(store instanceof Store, 'expected `%s.stores.%s` to be a `Store` but got `%s`', field, index, store);
    });
  });
  return dependencies;
}
function dependencyPropTypes(dependencies, existingPropTypes = {}) {
  const unrelatedPropTypes = oReduce(existingPropTypes, (keep, type, name) => {
    if (!dependencies.hasOwnProperty(name)) {
      keep[name] = type;
    }
    return keep;
  }, {});
  return oReduce(dependencies, (types, dependency) => {
    if (dependency instanceof Store) {
      return types;
    }
    // eslint-disable-next-line react-app/react/forbid-foreign-prop-types
    const {
      propTypes
    } = dependency;
    if (!propTypes || typeof propTypes !== 'object') {
      return types;
    }
    return oMerge(types, propTypes);
  }, unrelatedPropTypes);
}
function calculate(dependency, props, state) {
  if (dependency instanceof Store) {
    return dependency.get();
  }
  const {
    deref,
    stores
  } = dependency;
  return deref(props, state, stores);
}
function calculateInitial(dependencies, props, state) {
  return oMap(dependencies, dependency => calculate(dependency, props, state));
}
function calculateForDispatch(dependencies, dependencyIndexEntry, props, state) {
  return oMap(dependencyIndexEntry.fields, (_, field) => calculate(dependencies[field], props, state));
}
function makeIndexEntry() {
  return {
    fields: {},
    dispatchTokens: {}
  };
}
function makeDependencyIndex(dependencies) {
  enforceValidDependencies(dependencies);
  return oReduce(dependencies, (index, dep, field) => {
    const stores = dep instanceof Store ? [dep] : dep.stores;
    stores.forEach(store => {
      getActionTypes(store).forEach(actionType => {
        let entry = index[actionType];
        if (!entry) {
          entry = index[actionType] = makeIndexEntry();
        }
        const token = getDispatchToken(store);
        entry.dispatchTokens[token] = true;
        entry.fields[field] = true;
      });
    });
    return index;
  }, {});
}
function makeDisplayName(prefix, BaseComponent) {
  const baseComponentName = BaseComponent.displayName || BaseComponent.name || 'Component';
  return `${prefix}(${baseComponentName})`;
}
function focuser(instance, ...args) {
  if (!instance.wrappedInstance || typeof instance.wrappedInstance.focus !== 'function') {
    return undefined;
  }
  return instance.wrappedInstance.focus(...args);
}
function waitForStores(dispatcher, tokens) {
  dispatcher.waitFor(tokens);
}
function handleDispatch(dispatcher, dependencyIndex, then, payload) {
  const actionType = payload.actionType || payload.type;
  if (!dependencyIndex[actionType]) {
    return;
  }
  const entry = dependencyIndex[actionType];
  waitForStores(dispatcher, Object.keys(entry.dispatchTokens));
  then(entry);
}
function connect(dependencies, dispatcher = get()) {
  enforceDispatcher(dispatcher);
  const dependencyIndex = makeDependencyIndex(dependencies);
  return function connector(BaseComponent) {
    class ConnectedComponent extends Component {
      constructor(props) {
        super(props);
        this.state = {};
        this.wrappedInstance = null;
        this.focus = BaseComponent.prototype && typeof BaseComponent.prototype.focus === "function" ? (...args) => focuser(this, ...args) : undefined;
        this.setWrappedInstance = ref => {
          this.wrappedInstance = ref;
        };
        if (dispatcher) {
          this.dispatchToken = dispatcher.register(handleDispatch.bind(null, dispatcher, dependencyIndex, this.handleDispatch.bind(this)));
        }
        this.state = calculateInitial(dependencies, this.props, {});
      }
      static getDerivedStateFromProps(props, state) {
        return calculateInitial(dependencies, props, state);
      }
      componentWillUnmount() {
        const dispatchToken = this.dispatchToken;
        if (dispatcher && dispatchToken) {
          this.dispatchToken = null;
          dispatcher.unregister(dispatchToken);
        }
      }
      handleDispatch(entry) {
        this.setState(calculateForDispatch(dependencies, entry, this.props, this.state));
      }
      render() {
        const refProps = typeof BaseComponent === 'function' ? {} : {
          ref: this.setWrappedInstance
        };
        return /*#__PURE__*/createElement(BaseComponent, Object.assign({}, this.props, this.state, refProps));
      }
    }
    ConnectedComponent.defaultProps = BaseComponent.defaultProps;
    ConnectedComponent.dependencies = dependencies;
    ConnectedComponent.displayName = makeDisplayName('Connected', BaseComponent);
    ConnectedComponent.propTypes = dependencyPropTypes(dependencies,
    // eslint-disable-next-line react-app/react/forbid-foreign-prop-types
    BaseComponent.propTypes);
    ConnectedComponent.WrappedComponent = BaseComponent;
    return hoistStatics(ConnectedComponent, BaseComponent);
  };
}
function subscribe(dependencies, dependencyIndex, dispatcher, callback, props = {}, state = null) {
  let dispatchToken;
  let storeState = {};
  function remove() {
    if (dispatchToken) {
      dispatcher.unregister(dispatchToken);
      dispatchToken = null;
    }
  }
  function handleUpdate(entry) {
    const prevStoreState = storeState;
    try {
      storeState = Object.assign(Object.assign({}, prevStoreState), calculateForDispatch(dependencies, entry, props, state));
      callback(null, storeState, prevStoreState, remove);
    } catch (error) {
      remove();
      callback(error, storeState, prevStoreState, remove);
    }
  }
  dispatchToken = dispatcher.register(handleDispatch.bind(null, dispatcher, dependencyIndex, handleUpdate));
  try {
    storeState = calculateInitial(dependencies, props, state);
    callback(null, storeState, {}, remove);
  } catch (error) {
    remove();
    callback(error, storeState, {}, remove);
  }
  return {
    remove
  };
}
function connectCallback(dependencies, dispatcher = get()) {
  enforceDispatcher(dispatcher);
  if (!dispatcher) {
    return null;
  }
  return subscribe.bind(null, dependencies, makeDependencyIndex(dependencies), dispatcher);
}
function useCurrent(value) {
  const ref = useRef(value);
  ref.current = value;
  return ref;
}
function _useDispatchSubscription(dependencyMap, currentProps, dispatcher, dependencyValue, setDependencyValue) {
  useEffect(() => {
    const dependencyIndex = makeDependencyIndex(dependencyMap);
    const dispatchToken = dispatcher.register(handleDispatch.bind(null, dispatcher, dependencyIndex, entry => {
      const newValue = calculateForDispatch(dependencyMap, entry, currentProps.current, dependencyValue);
      if (Object.keys(newValue).map(k => shallowEqual(newValue[k], dependencyValue[k])).some(el => !el)) {
        setDependencyValue(newValue);
      }
    }));
    return () => {
      dispatcher.unregister(dispatchToken);
    };
  }, [currentProps, dependencyMap, dependencyValue, dispatcher, setDependencyValue]);
}
function useStoreDependency(dependency, props, dispatcher = get()) {
  enforceDispatcher(dispatcher);
  const newValue = {
    dependency: calculate(dependency, props)
  };
  const [dependencyValue, setDependencyValue] = useState(newValue);
  const currProps = useCurrent(props);
  const dependencyMap = useMemo(() => ({
    dependency
  }), [dependency]);
  _useDispatchSubscription(dependencyMap, currProps, dispatcher, dependencyValue, setDependencyValue);
  if (!shallowEqual(newValue.dependency, dependencyValue.dependency)) {
    setDependencyValue(newValue);
    return newValue.dependency;
  }
  return dependencyValue.dependency;
}
function define() {
  return new StoreSingleton();
}
function defineFactory() {
  return new StoreFactory({});
}
export { DispatcherInstance, InspectStore, StoreFactory, connect, connectCallback, define, defineFactory, useStoreDependency };